import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { ReferenceMarkup } from './ReferenceMarkup';
import { createReferenceViews } from './referenceViews';
import { getPage, productById, routeTo } from './products';
import { Mission } from './Mission';
import { Calculator } from './Calculator';
import { Dialog } from './Dialog';
import { EnquiryForm } from './EnquiryForm';
import { useMotion } from './useMotion';
import { useMetadata } from './useMetadata';
import './reference.css';
import './refinements.css';

export default function V3Site() {
  const { lang, toggleLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const root = useRef(null);
  const [selected, setSelected] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [hotspot, setHotspot] = useState(0);
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState({ name: '', phone: '', location: '', intent: 'general', drone: '' });
  const page = getPage(location.pathname);
  const tr = useCallback((mr, en) => lang === 'mr' ? mr : en, [lang]);
  const views = useMemo(() => createReferenceViews(lang, selected), [lang, selected]);
  useMotion(root, `${page}-${lang}`);
  useMetadata(page, lang);

  useEffect(() => {
    document.body.classList.add('v3-active');
    return () => document.body.classList.remove('v3-active');
  }, []);
  useEffect(() => {
    setMenuOpen(false); setModal(null); setTab(0); setHotspot(0);
    const hash = location.hash.slice(1);
    if (['home', 'products', 'flower', ...Object.keys(productById)].includes(hash)) {
      navigate(routeTo(hash), { replace: true }); return;
    }
    const aliases = { rtk: 'mission', videos: 'stories', training: 'service', subsidy: 'income', 'super-compact': 'models' };
    let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => {
      const target = document.getElementById(aliases[hash] || hash);
      if (target) target.scrollIntoView({ block: 'start', behavior: 'instant' });
      else window.scrollTo({ top: 0, behavior: 'instant' });
    }); });
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.hash, navigate]);
  useEffect(() => {
    if (!menuOpen) return;
    const key = e => { if (e.key === 'Escape') { setMenuOpen(false); root.current?.querySelector('.menu')?.focus(); } };
    const outside = e => { if (!e.target.closest('header')) setMenuOpen(false); };
    document.addEventListener('keydown', key); document.addEventListener('click', outside);
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('click', outside); };
  }, [menuOpen]);
  useEffect(() => {
    const track = root.current?.querySelector('.kd-marquee-track');
    if (!track || typeof track.animate !== 'function') return;
    let anim;
    const frame = requestAnimationFrame(() => {
      const half = track.scrollWidth / 2;
      if (!half) return;
      anim = track.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(-${half}px)` }],
        { duration: 30000, iterations: Infinity, easing: 'linear' }
      );
    });
    return () => { cancelAnimationFrame(frame); anim?.cancel(); };
  }, [lang, page]);
  const onAction = (name, args, event) => {
    const actions = {
      navigate: () => navigate(routeTo(args[0])),
      toggleLang,
      toggleMenu: () => setMenuOpen(value => !value),
      closeMenu: () => setMenuOpen(false),
      enquire: () => {
        const intent = args[0] === 'currentIntent()' ? page === 'flower' ? 'attachment' : productById[page] ? page : 'general' : args[0];
        setDraft(value => ({ ...value, intent: views.intents[intent] ? intent : 'general' }));
        setMenuOpen(false); setModal({ kind: 'enquiry' });
      },
      videos: () => setModal({ kind: 'videos' }),
      guidance: () => setModal({ kind: 'guidance', mode: args[0] }),
      calcModal: () => setModal({ kind: 'calculator', mode: args[0] || 'agri' }),
      comparison: () => { if (selected.size >= 2) setModal({ kind: 'comparison' }); },
      selectCompare: () => {
        if (event.target.checked && selected.size === 3) { event.target.checked = false; setModal({ kind: 'limit' }); return; }
        setSelected(previous => { const next = new Set(previous); event.target.checked ? next.add(args[0]) : next.delete(args[0]); return next; });
      },
      hotspot: () => setHotspot(args[0]),
      detailTab: () => setTab(args[0]),
    };
    actions[name]?.();
  };
  const markup = (html, prefix, options = {}) => <ReferenceMarkup html={html} prefix={prefix} tr={tr} onAction={onAction} {...options} />;
  const panels = {
    'hot-note': () => ({ children: markup(views.hotContent(hotspot), 'hotspot-description') }),
    hotspot: a => ({ props: { 'aria-pressed': a.onclick.startsWith(`hotspot(${hotspot},`) } }),
    'info-panel': () => ({ props: { 'aria-labelledby': `tab-${tab}`, tabIndex: 0 }, children: markup(views.detailContent(tab, page), 'product-panel') }),
    ...Object.fromEntries([0, 1, 2].map(i => [`tab-${i}`, () => ({ props: { 'aria-selected': i === tab, tabIndex: i === tab ? 0 : -1, onKeyDown: e => {
      let next = i;
      if (e.key === 'ArrowRight') next = (i + 1) % 3;
      else if (e.key === 'ArrowLeft') next = (i + 2) % 3;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = 2;
      else return;
      e.preventDefault(); setTab(next); document.getElementById(`tab-${next}`)?.focus();
    } } })])),
  };
  const content = page === 'products' ? views.catalog() : page === 'flower' ? views.flower() : productById[page] ? views.dronePage(page) : views.home();
  const main = <main id="main" data-testid={`page-${page}`}>{markup(content, page, { overrides: panels, components: { mission: () => <Mission key="mission" lang={lang} />, calculator: () => <Calculator key="flower-page-calculator" mode="flower" lang={lang} /> } })}</main>;

  let descriptor;
  if (modal?.kind === 'enquiry') descriptor = { title: tr('तुमची गरज सांगा', 'Tell us what you need'), content: <EnquiryForm lang={lang} draft={draft} setDraft={setDraft} /> };
  if (modal?.kind === 'limit') descriptor = { title: tr('तीन मॉडेलपर्यंत तुलना', 'Compare up to three models'), content: <p data-testid="comparison-limit-message">{tr('एक मॉडेल काढून दुसरे निवडा.', 'Remove one model before selecting another.')}</p> };
  if (['videos', 'comparison', 'guidance', 'calculator'].includes(modal?.kind)) {
    descriptor = modal.kind === 'calculator' ? views.calcModal(modal.mode) : modal.kind === 'guidance' ? views.guidance(modal.mode) : views[modal.kind]();
    descriptor.content = <div className={modal.kind === 'comparison' ? 'comparison-scroll' : undefined} data-testid={`dialog-${modal.kind}-content`}>{markup(descriptor.body, 'modal', { components: { calculator: () => <Calculator key={modal.mode} mode={modal.mode} lang={lang} /> } })}</div>;
  }
  return <div className="v3-site" ref={root} data-testid="kisan-v3-site">
    <a className="skip-link" href="#main" data-testid="skip-to-content">{tr('मुख्य मजकुराकडे जा', 'Skip to content')}</a>
    {markup(views.shell(), 'shell', { components: { main: () => main }, overrides: { nav: () => ({ props: { className: menuOpen ? 'open' : '' } }), menu: () => ({ props: { 'aria-expanded': menuOpen, 'aria-controls': 'nav' } }) } })}
    {descriptor && <Dialog title={descriptor.title} wide={descriptor.wide} lang={lang} onClose={() => setModal(null)}>{descriptor.content}</Dialog>}
  </div>;
}